import { useEffect, useState, useSyncExternalStore } from "react";
import type { WebNotifications } from "./notifications";
import { track } from "./analytics";
import type { View } from "./nav";
import { Mascot, Sheet } from "./ui";

/**
 * Right after a first pairing, when the person reaches the main screen, ask once whether to turn alerts on. Pairing sets the
 * flag; this takes it. The permission prompt itself needs a tap, so the button is what asks the browser. Nothing is asked where
 * alerts can't work (an iPhone browser tab) or were already decided.
 */
const KEY = "foreman.alerts.ask";
const MAIN = new Set<View["name"]>(["sessions", "home", "computers", "inbox"]);

export function askAboutAlertsAfterPairing(): void {
  try {
    if (localStorage.getItem(KEY) !== "off") localStorage.setItem(KEY, "1"); // "off": never ask (set by the browser tests)
  } catch {
    /* no storage: not asked */
  }
}
const pending = (): boolean => {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
};
const clear = () => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
};

export function AlertsAsk({ notifications, view }: { notifications: WebNotifications; view: View }) {
  const state = useSyncExternalStore(notifications.subscribe, notifications.getState);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const main = MAIN.has(view.name);
  useEffect(() => {
    if (!main || !pending()) return;
    if (state !== "off") return void clear(); // already on, blocked, or not possible here: nothing to ask
    const t = setTimeout(() => {
      setOpen(true);
      track("alerts_ask_shown");
    }, 700);
    return () => clearTimeout(t);
  }, [main, state]);
  if (!open || state !== "off") return null;
  const close = (result: string) => {
    clear();
    setOpen(false);
    track("alerts_ask_answered", { result });
  };
  return (
    <Sheet onClose={() => close("dismissed")} top center>
      <div className="gate-modal" data-alerts-ask>
        <Mascot size={84} />
        <div className="t">Get alerts when an agent needs you</div>
        <p className="fine">Approvals, questions and finished tasks reach you even when Brocoded is closed.</p>
        <div className="gate-actions">
          <button
            className="btn primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              const next = await notifications.enable();
              setBusy(false);
              close(next === "on" ? "on" : next);
            }}
          >
            Turn on alerts
          </button>
          <button className="text-btn plain" onClick={() => close("not_now")}>
            Not now
          </button>
        </div>
      </div>
    </Sheet>
  );
}
